import Logo from '@/../../public/storage/Logo.webp';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import Typography from '@/components/ui/typography';
import { CategoryContext } from '@/lib/CategoryContext';
import { cn, inquiryHref } from '@/lib/utils';
import { NavLink, PageProps } from '@/types';
import { Link, router, usePage } from '@inertiajs/react';
import * as AccessibleIcon from '@radix-ui/react-accessible-icon';
import {
  ArrowRight,
  Menu,
  MoveRight,
  SendHorizonal,
  Sparkles,
} from 'lucide-react';
import { useContext, useState, useEffect, useCallback, useMemo } from 'react';

import { Routes } from './Routes';

type WithRequiredProperty<Type, Key extends keyof Type> = Type & {
  [Property in Key]-?: Type[Property];
};

const enquirePillClassName =
  'group inline-flex h-12 w-full items-center justify-center gap-3 rounded-full border-2 border-primary/30 pl-1.5 pr-5 transition-colors duration-300 hover:border-primary hover:bg-primary hover:text-primary-foreground';
const enquireBadgeClassName =
  'inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors duration-300 group-hover:bg-primary-foreground group-hover:text-primary';

/**
 * A node's href is built from its *parent's* `sublink_route`: top-level entries
 * are Ziggy route names, everything below them is a slug passed to the route
 * the parent names.
 */
const hrefFor = (node: NavLink, parent?: NavLink) =>
  parent?.sublink_route
    ? route(parent.sublink_route, node.path)
    : route(node.path);

const Drawer = ({ navLinks }: { navLinks: NavLink[] }) => {
  const drawerEnquireHref = inquiryHref();
  const { url, props } = usePage<PageProps>();

  const deepLinks = useCallback(
    (
      link: WithRequiredProperty<NavLink, 'sublinks'>,
      parent: NavLink | undefined
    ) => {
      return (
        <Accordion
          key={link.label}
          type="single"
          collapsible
          className="w-full pt-2"
        >
          <AccordionItem value={link.label} className="border-none">
            <div className="inline-flex w-full items-center justify-between divide-x">
              <SheetClose asChild key={link.label}>
                <Button
                  asChild
                  variant="ghost"
                  className="w-full justify-start font-semibold capitalize underline-offset-4 hover:text-primary hover:underline"
                >
                  <Link
                    href={hrefFor(link, parent)}
                    className={
                      url.endsWith(`/${link.path}`) ? 'text-primary' : ''
                    }
                  >
                    {link.label}
                  </Link>
                </Button>
              </SheetClose>
              <AccordionTrigger className="shrink-0 py-2 pl-2 pr-4 text-lg [&>svg]:size-5" />
            </div>
            <AccordionContent className="pl-2">
              {link.sublinks.map((sublink) =>
                sublink.sublinks && sublink.sublinks.length > 0 ? (
                  deepLinks(
                    sublink as WithRequiredProperty<NavLink, 'sublinks'>,
                    link
                  )
                ) : (
                  <SheetClose asChild key={sublink.label} className="w-full">
                    <Button
                      asChild
                      variant="ghost"
                      className="justify-start font-semibold capitalize underline-offset-4 hover:text-primary hover:underline"
                    >
                      <Link
                        href={hrefFor(sublink, link)}
                        className={
                          url.includes(`/${link.path}/${sublink.path}`)
                            ? 'text-primary'
                            : ''
                        }
                      >
                        {sublink.label}
                      </Link>
                    </Button>
                  </SheetClose>
                )
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      );
    },
    [url]
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        {/* Kept at every size: the drawer is the only route to the account
            actions and to Career / FAQ / Blogs, which the five primary
            desktop links leave out. */}
        <Button variant="ghost" size="icon">
          <AccessibleIcon.Root label="Navigation Links">
            <Menu size={24} className="text-foreground" />
          </AccessibleIcon.Root>
        </Button>
      </SheetTrigger>
      <SheetContent
        className="flex w-80 flex-col gap-0 px-0 pb-8 pt-0"
        aria-describedby=""
      >
        <SheetHeader className="items-start px-5 py-3">
          <SheetTitle>
            <img
              className="h-10 w-auto object-contain"
              alt="Samuthrika Academy Logo"
              src={Logo}
              width={300}
              height={128}
            />
          </SheetTitle>
        </SheetHeader>
        <div className="flex h-full flex-col gap-y-5 overflow-hidden border-t">
          <div className="flex flex-col gap-y-4 px-5 pt-5">
            {props.auth.user ? (
              <>
                <Typography.Small>Signed in as</Typography.Small>
                <Typography.Large className="text-primary">
                  {props.auth.user.first_name} {props.auth.user.last_name}
                </Typography.Large>
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    onClick={() =>
                      router.post(route('logout'), {}, { replace: true })
                    }
                  >
                    Log out
                  </Button>
                </SheetClose>
              </>
            ) : (
              <>
                <SheetClose asChild>
                  <Button asChild>
                    <Link href={route('login')}>Log in</Link>
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button asChild variant="outline">
                    <Link href={route('register')}>Create account</Link>
                  </Button>
                </SheetClose>
              </>
            )}
          </div>
          <div aria-hidden className="seam mx-5" />
          <div className="flex min-h-0 flex-1 flex-col gap-y-1 overflow-auto px-5 pb-4">
            {navLinks.map((link) =>
              link.sublinks && link.sublinks.length > 0 ? (
                deepLinks(
                  link as WithRequiredProperty<NavLink, 'sublinks'>,
                  undefined
                )
              ) : (
                <div key={link.label} className="w-full pt-2">
                  <SheetClose asChild>
                    <Button
                      asChild
                      variant="ghost"
                      className="w-full justify-start font-semibold capitalize underline-offset-4 hover:text-primary hover:underline"
                    >
                      <Link
                        href={route(link.path)}
                        className={
                          url.includes(`/${link.path}`) ? 'text-primary' : ''
                        }
                      >
                        {link.label}
                        <ArrowRight
                          size={20}
                          strokeWidth={1.5}
                          className="ml-auto"
                        />
                      </Link>
                    </Button>
                  </SheetClose>
                </div>
              )
            )}
            <div className="w-full pt-2">
              <SheetClose asChild>
                <Button
                  asChild
                  variant="ghost"
                  className="w-full justify-start font-semibold capitalize underline-offset-4 hover:text-primary hover:underline"
                >
                  <a
                    href="/blog"
                    className={url.includes(`/blog`) ? 'text-primary' : ''}
                  >
                    Blogs
                    <ArrowRight
                      size={20}
                      strokeWidth={1.5}
                      className="ml-auto"
                    />
                  </a>
                </Button>
              </SheetClose>
            </div>
          </div>

          <div className="flex flex-col items-center gap-3 border-t px-5 pt-5 text-center">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Have a question?
            </span>
            <SheetClose asChild>
              {drawerEnquireHref.startsWith('#') ? (
                <a href={drawerEnquireHref} className={enquirePillClassName}>
                  <span className={enquireBadgeClassName}>
                    <SendHorizonal size={16} strokeWidth={2.25} />
                  </span>
                  <span className="font-semibold">Enquire</span>
                </a>
              ) : (
                <Link href={drawerEnquireHref} className={enquirePillClassName}>
                  <span className={enquireBadgeClassName}>
                    <SendHorizonal size={16} strokeWidth={2.25} />
                  </span>
                  <span className="font-semibold">Enquire</span>
                </Link>
              )}
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Desktop dropdown for one top-level entry. Sub-navigation used to be reachable
 * only through the mobile drawer, so on desktop the shoppe's groups, the course
 * categories and the service list were effectively invisible.
 */
const MegaPanel = ({ link }: { link: NavLink }) => {
  const columns = link.sublinks ?? [];

  return (
    <div className="grid w-[min(88vw,42rem)] grid-cols-2 gap-x-8 gap-y-5 p-6 lg:grid-cols-3">
      {columns.map((column) => (
        <div key={column.path} className="flex flex-col gap-1.5">
          <NavigationMenuLink asChild>
            <Link
              href={hrefFor(column, link)}
              className="text-sm font-semibold capitalize text-foreground transition-colors hover:text-primary"
            >
              {column.label}
            </Link>
          </NavigationMenuLink>
          {column.sublinks && column.sublinks.length > 0 && (
            <ul className="flex flex-col gap-1">
              {column.sublinks.slice(0, 5).map((child) => (
                <li key={child.path}>
                  <NavigationMenuLink asChild>
                    <Link
                      href={hrefFor(child, column)}
                      className="text-sm capitalize text-muted-foreground transition-colors hover:text-primary"
                    >
                      {child.label}
                    </Link>
                  </NavigationMenuLink>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <div className="col-span-full">
        <div aria-hidden className="seam mb-4" />
        <NavigationMenuLink asChild>
          <Link
            href={route(link.path)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
          >
            All {link.label}
            <MoveRight size={16} />
          </Link>
        </NavigationMenuLink>
      </div>
    </div>
  );
};

const NavBar = () => {
  const categories = useContext(CategoryContext);
  const enquireHref = inquiryHref();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /*
   * Merge the fetched categories into the static route tree.
   *
   * This used to assign into `Routes` by hardcoded index (`[1]`, `[3]`, `[4]`)
   * through a shallow copy, so reordering Routes.ts silently rewired the nav
   * and Footer — which imports the same array — saw the mutation. Match on
   * `path` and build a new tree instead.
   */
  const navLinks: NavLink[] = useMemo(
    () =>
      Routes.map((link) => {
        switch (link.path) {
          case 'courses':
            return {
              ...link,
              sublinks: categories.courses.map((category) => ({
                label: category.title,
                path: category.slug,
              })),
            };
          case 'shoppe':
            return {
              ...link,
              sublinks: categories.shoppe.map((group) => ({
                label: group.name,
                path: group.slug,
                sublink_route: 'shoppe.showCategory',
                sublinks: group.categories.map((category) => ({
                  label: category.name,
                  path: category.slug,
                  sublink_route: 'shoppe.showSubCategory',
                  sublinks: category.sub_categories.map((subCategory) => ({
                    label: subCategory.name,
                    path: subCategory.slug,
                  })),
                })),
              })),
            };
          case 'services':
            return {
              ...link,
              sublinks: categories.services.map((category) => ({
                label: category.title,
                path: category.slug,
              })),
            };
          default:
            return link;
        }
      }),
    [categories]
  );

  const primaryLinks = navLinks.filter((link) => link.path !== 'summer-camp');
  const currentRoute = route().current();

  return (
    <>
      {/*
        Announcement bar. Scrolls away rather than sticking, so the sticky bar
        below it has one fixed height that heroes can size against.

        On the brand gradient rather than ink: it sat directly above a pale
        navbar and read as a black slab across the top of every page, and the
        arrow inside it was text-primary — orange on near-black, the weakest
        pairing on the site. Orange makes it read as an announcement rather than
        as chrome, and bookends the rule above the footer.

        Ink text, not white: --primary-foreground over the amber end of the
        gradient measures 1.91:1. Ink holds 5.39:1 at the orange end and 9.23:1
        at the amber.
      */}
      {route().has('summer-camp') && !route().current('summer-camp') && (
        <div className="bg-brand-gradient relative w-full overflow-hidden text-ink">
          <div
            aria-hidden
            className="pattern-weave pattern-on-brand absolute inset-0"
          />

          <div className="relative mx-auto flex max-w-7xl items-center justify-center px-5 py-2.5 md:px-8 lg:px-12">
            <Link
              href={route('summer-camp')}
              className="group inline-flex items-center gap-2.5 text-center text-sm font-semibold"
            >
              <Sparkles size={16} className="shrink-0" />
              Summer camp enrolment is open
              <MoveRight
                size={16}
                className="shrink-0 transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      )}

      <nav
        className={cn(
          'sticky top-0 z-50 w-full backdrop-blur-md transition-all duration-300',
          scrolled
            ? 'border-gradient-b bg-background/90 shadow-card'
            : 'bg-background/70'
        )}
      >
        <div className="mx-auto flex h-18 max-w-7xl items-center justify-between gap-4 px-5 md:px-8 lg:px-12">
          <Link href={route('home')} className="shrink-0">
            {/*
              72px bar, 64px logo, at every scroll position.

              The bar previously ran 96-112px and shrank to 64px on scroll, with
              the logo going 64-80px down to 44px. A 64px logo inside a 72px bar
              leaves 4px of clearance, so there is no room left to shrink into —
              the height is now fixed and `scrolled` drives only the background,
              border and shadow.
            */}
            <img
              className="h-16 w-auto object-contain"
              alt="Samuthrika Academy — ISO 9001:2015 certified academy in Trichy"
              src={Logo}
              width={300}
              height={128}
            />
          </Link>

          <NavigationMenu className="hidden md:flex">
            <NavigationMenuList>
              {primaryLinks.slice(0, 5).map((link) => {
                const active = currentRoute?.startsWith(link.path) ?? false;
                const hasPanel = (link.sublinks?.length ?? 0) > 0;

                return (
                  <NavigationMenuItem key={link.label}>
                    {hasPanel ? (
                      <>
                        <NavigationMenuTrigger
                          className={cn(active && 'text-primary')}
                        >
                          <span className="relative">
                            {link.label}
                            {active && (
                              <span
                                aria-hidden
                                className="seam absolute -bottom-1.5 left-0 w-full"
                              />
                            )}
                          </span>
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <MegaPanel link={link} />
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <NavigationMenuLink asChild>
                        <Link
                          href={route(link.path)}
                          className={cn(
                            navigationMenuTriggerStyle(),
                            active && 'text-primary'
                          )}
                        >
                          <span className="relative">
                            {link.label}
                            {active && (
                              <span
                                aria-hidden
                                className="seam absolute -bottom-1.5 left-0 w-full"
                              />
                            )}
                          </span>
                        </Link>
                      </NavigationMenuLink>
                    )}
                  </NavigationMenuItem>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>

          <div className="flex items-center gap-2">
            {/* Went to route('contact'), which landed at the top of the page
                rather than on the form the label promises. */}
            <Button
              asChild
              size="sm"
              className="hidden rounded-full px-5 md:inline-flex"
            >
              {enquireHref.startsWith('#') ? (
                <a href={enquireHref}>Enquire</a>
              ) : (
                <Link href={enquireHref}>Enquire</Link>
              )}
            </Button>
            <Drawer navLinks={navLinks} />
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar;
